import React, { useCallback, useEffect, useMemo, useState } from "react";

import axios from "axios";

import { Activity, Loader2, RefreshCw, ScrollText, Shield, Trash2, Users } from "lucide-react";

import { toast } from "sonner";

import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";

import { Badge } from "@/components/ui/badge";

import { Checkbox } from "@/components/ui/checkbox";

import CdrTablePagination from "@/components/cdr/CdrTablePagination";

import {

  PanelTableBody, PanelTableCell, PanelTableFrame, PanelTableHead, PanelTableHeadRow, PanelTableHeader, PanelTableRow,

} from "@/components/ui/panel-table";



const PAGE_SIZE = 25;



const EVENT_LABELS = {

  login_success: "Giriş",

  login_failed: "Başarısız",

  login_blocked: "Engellendi",

  logout: "Çıkış",

};



const METHOD_LABELS = {

  password: "Şifre",

  sms: "SMS",

  whatsapp: "WhatsApp",

  totp: "Authenticator",

  remember: "Beni hatırla",

  link: "Mobil link",

  session: "Oturum",

  "2fa": "2FA",

};



const SecurityAuditLogs = ({ apiBase, isDark, textClass, labelClass }) => {

  const [tab, setTab] = useState("login");

  const [loading, setLoading] = useState(true);

  const [deleting, setDeleting] = useState(false);

  const [items, setItems] = useState([]);

  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");

  const [startDate, setStartDate] = useState("");

  const [endDate, setEndDate] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);



  const panelClass = isDark ? "border-slate-800 bg-slate-900/60" : "border-gray-200 bg-white";

  const inputClass = isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-gray-200";

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));



  const filterParams = useMemo(() => ({

    search: search.trim() || undefined,

    start_date: startDate || undefined,

    end_date: endDate || undefined,

  }), [search, startDate, endDate]);



  const loadLogs = useCallback(async () => {

    setLoading(true);

    try {

      const res = await axios.get(`${apiBase}/audit-logs`, {

        params: {

          type: tab === "api" ? "api" : "login",

          limit: PAGE_SIZE,

          offset: (page - 1) * PAGE_SIZE,

          ...filterParams,

        },

      });

      setItems(res.data?.items || []);

      setTotal(res.data?.total || 0);

    } catch (err) {

      toast.error(err.response?.data?.detail || "Loglar yüklenemedi");

    } finally {

      setLoading(false);

    }

  }, [apiBase, tab, page, filterParams]);



  useEffect(() => { loadLogs(); }, [loadLogs]);



  useEffect(() => {

    setPage(1);

    setSelectedIds([]);

  }, [tab, search, startDate, endDate]);



  useEffect(() => {

    setSelectedIds([]);

  }, [page]);



  const pageIds = useMemo(() => items.map((row) => row.id), [items]);

  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));



  const toggleSelectAll = () => {

    if (allPageSelected) {

      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));

    } else {

      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);

    }

  };



  const toggleSelectOne = (id) => {

    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  };



  const handleDelete = async ({ ids = [], deleteFiltered = false, deleteAll = false } = {}) => {

    let confirmText = "";

    if (deleteAll) {

      confirmText = tab === "api"

        ? "Tüm API logları kalıcı olarak silinecek. Devam edilsin mi?"

        : "Tüm kullanıcı giriş logları kalıcı olarak silinecek. Devam edilsin mi?";

    } else if (deleteFiltered) {

      confirmText = `Mevcut filtreye uyan ${total.toLocaleString("tr-TR")} kayıt silinecek. Devam edilsin mi?`;

    } else {

      confirmText = `${ids.length} kayıt silinecek. Devam edilsin mi?`;

    }

    if (!window.confirm(confirmText)) return;



    setDeleting(true);

    try {

      const payload = {

        type: tab === "api" ? "api" : "login",

        ...(deleteAll ? { delete_all: true } : deleteFiltered ? { delete_filtered: true, ...filterParams } : { ids }),

      };

      const res = await axios.delete(`${apiBase}/audit-logs`, { data: payload });

      toast.success(res.data?.message || "Kayıtlar silindi");

      setSelectedIds([]);

      if (deleteAll || deleteFiltered) setPage(1);

      await loadLogs();

    } catch (err) {

      toast.error(err.response?.data?.detail || "Silme işlemi başarısız");

    } finally {

      setDeleting(false);

    }

  };



  const formatDate = (value) => {

    if (!value) return "—";

    try {

      return new Date(value.replace(" ", "T")).toLocaleString("tr-TR");

    } catch {

      return value;

    }

  };



  return (

    <div className={`rounded-2xl border overflow-hidden ${panelClass}`}>

      <div className={`px-4 py-4 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 ${isDark ? "border-slate-800" : "border-gray-100"}`}>

        <div className="flex items-center gap-3">

          <div className={`p-2 rounded-xl ${isDark ? "bg-violet-500/15 text-violet-400" : "bg-violet-50 text-violet-600"}`}>

            <ScrollText className="w-5 h-5" />

          </div>

          <div>

            <h3 className={`text-base font-semibold ${textClass}`}>Güvenlik Logları</h3>

            <p className={`text-xs ${labelClass}`}>Kullanıcı giriş/çıkış ve API istek kayıtları</p>

          </div>

        </div>

        <div className="flex flex-wrap gap-2">

          <Button type="button" size="sm" variant={tab === "login" ? "default" : "outline"} onClick={() => setTab("login")} className={tab !== "login" && isDark ? "border-slate-700" : ""}>

            <Users className="w-4 h-4 mr-1.5" /> Kullanıcı Logu

          </Button>

          <Button type="button" size="sm" variant={tab === "api" ? "default" : "outline"} onClick={() => setTab("api")} className={tab !== "api" && isDark ? "border-slate-700" : ""}>

            <Activity className="w-4 h-4 mr-1.5" /> API Logları

          </Button>

          <Button type="button" size="sm" variant="outline" onClick={loadLogs} disabled={loading || deleting} className={isDark ? "border-slate-700" : ""}>

            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}

          </Button>

        </div>

      </div>



      <div className={`px-4 py-3 border-b flex flex-col md:flex-row gap-2 ${isDark ? "border-slate-800 bg-slate-950/20" : "border-gray-100 bg-gray-50/70"}`}>

        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kullanıcı, IP, path..." className={`h-9 ${inputClass}`} />

        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`h-9 md:w-40 ${inputClass}`} />

        <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`h-9 md:w-40 ${inputClass}`} />

        <Button type="button" size="sm" onClick={() => { setPage(1); loadLogs(); }} className="h-9">Filtrele</Button>

      </div>



      {(selectedIds.length > 0 || total > 0) && (

        <div className={`px-4 py-2 border-b flex flex-wrap items-center gap-2 ${isDark ? "border-slate-800 bg-slate-950/30" : "border-gray-100 bg-gray-50/80"}`}>

          {selectedIds.length > 0 && (

            <>

              <span className={`text-xs ${labelClass}`}>{selectedIds.length} seçili</span>

              <Button type="button" size="sm" variant="destructive" disabled={deleting} onClick={() => handleDelete({ ids: selectedIds })} className="h-8">

                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}

                Seçilenleri sil

              </Button>

            </>

          )}

          {total > 0 && (

            <Button

              type="button"

              size="sm"

              variant="outline"

              disabled={deleting}

              onClick={() => handleDelete({ deleteFiltered: true })}

              className={`h-8 ${isDark ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10" : "border-rose-300 text-rose-700 hover:bg-rose-50"}`}

            >

              Filtreye uyanları sil ({total.toLocaleString("tr-TR")})

            </Button>

          )}

        </div>

      )}



      {loading ? (

        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>

      ) : (

        <PanelTableFrame className="border-0 rounded-none">

          <PanelTableHeader>

            <PanelTableHeadRow>

              <PanelTableHead className="w-10">

                <Checkbox checked={allPageSelected} onCheckedChange={toggleSelectAll} aria-label="Sayfadaki tümünü seç" />

              </PanelTableHead>

              {tab === "login" ? (

                <>

                  <PanelTableHead>Zaman</PanelTableHead>

                  <PanelTableHead>Kullanıcı</PanelTableHead>

                  <PanelTableHead>Olay</PanelTableHead>

                  <PanelTableHead>Yöntem</PanelTableHead>

                  <PanelTableHead>IP</PanelTableHead>

                  <PanelTableHead>Cihaz</PanelTableHead>

                  <PanelTableHead>Durum</PanelTableHead>

                </>

              ) : (

                <>

                  <PanelTableHead>Zaman</PanelTableHead>

                  <PanelTableHead>Kullanıcı</PanelTableHead>

                  <PanelTableHead>Method</PanelTableHead>

                  <PanelTableHead>Path</PanelTableHead>

                  <PanelTableHead>IP</PanelTableHead>

                  <PanelTableHead>Cihaz</PanelTableHead>

                  <PanelTableHead>HTTP</PanelTableHead>

                </>

              )}

            </PanelTableHeadRow>

          </PanelTableHeader>

          <PanelTableBody>

            {items.length === 0 ? (

              <PanelTableRow><PanelTableCell colSpan={8} className={`text-center py-10 ${labelClass}`}>Kayıt yok</PanelTableCell></PanelTableRow>

            ) : tab === "login" ? items.map((row) => (

              <PanelTableRow key={row.id}>

                <PanelTableCell>

                  <Checkbox checked={selectedIds.includes(row.id)} onCheckedChange={() => toggleSelectOne(row.id)} aria-label={`Kayıt ${row.id} seç`} />

                </PanelTableCell>

                <PanelTableCell className="text-xs whitespace-nowrap">{formatDate(row.created_at)}</PanelTableCell>

                <PanelTableCell className="text-xs">

                  <div className="font-medium">{row.display_name || row.username || "—"}</div>

                  {row.username && row.display_name && <div className={labelClass}>{row.username}</div>}

                </PanelTableCell>

                <PanelTableCell className="text-xs">{EVENT_LABELS[row.event_type] || row.event_type}</PanelTableCell>

                <PanelTableCell className="text-xs">{METHOD_LABELS[row.auth_method] || row.auth_method}</PanelTableCell>

                <PanelTableCell className="text-xs font-mono">{row.ip_address}</PanelTableCell>

                <PanelTableCell className="text-xs">{row.device_label || "—"}</PanelTableCell>

                <PanelTableCell>

                  <Badge className={row.success ? "bg-emerald-500/15 text-emerald-600" : "bg-rose-500/15 text-rose-600"}>

                    {row.success ? "OK" : "Hata"}

                  </Badge>

                </PanelTableCell>

              </PanelTableRow>

            )) : items.map((row) => (

              <PanelTableRow key={row.id}>

                <PanelTableCell>

                  <Checkbox checked={selectedIds.includes(row.id)} onCheckedChange={() => toggleSelectOne(row.id)} aria-label={`Kayıt ${row.id} seç`} />

                </PanelTableCell>

                <PanelTableCell className="text-xs whitespace-nowrap">{formatDate(row.created_at)}</PanelTableCell>

                <PanelTableCell className="text-xs">{row.username || "—"}</PanelTableCell>

                <PanelTableCell className="text-xs font-mono">{row.http_method}</PanelTableCell>

                <PanelTableCell className="text-xs font-mono max-w-[280px] truncate" title={row.path}>{row.path}</PanelTableCell>

                <PanelTableCell className="text-xs font-mono">{row.ip_address}</PanelTableCell>

                <PanelTableCell className="text-xs">{row.device_label || "—"}</PanelTableCell>

                <PanelTableCell className="text-xs">

                  <Badge variant="outline">{row.status_code || "—"} · {row.duration_ms}ms</Badge>

                </PanelTableCell>

              </PanelTableRow>

            ))}

          </PanelTableBody>

        </PanelTableFrame>

      )}



      <CdrTablePagination

        isDark={isDark}

        page={page}

        pageSize={PAGE_SIZE}

        total={total}

        totalPages={totalPages}

        loading={loading || deleting}

        onPageChange={setPage}

      />

    </div>

  );

};



export default SecurityAuditLogs;

